import { Link, useParams } from "react-router-dom";

import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useAsyncEffect } from "./Hooks/AsyncEffect";
import { usePost } from "./API";
import { ModularQuestionPage, Sections } from "./Apply";
import { Alert, Button, Loading } from "@aidkitorg/component-library";
import { ThreeColumnPage } from "./Components/ThreeColumnPage";
import { ApplicantProvider } from "./Contexts/ApplicantContext";
import type { NavigationNode } from "@aidkitorg/types/lib/translation/permissions";
import type { Warning } from "@aidkitorg/aidkit/lib/program/searchForDuplicates";
import InterfaceContext from "./Context";

export default function IntakePage() {
  const { lang } = useContext(InterfaceContext);
  const { subsurveyPath, buttonId } = useParams<{ subsurveyPath: string, buttonId: string }>();

  const [subsurvey, setSubsurvey] = useState<Sections | undefined>();
  const [loading, setLoading] = useState(false);

  const [info, setInfo] = useState<Record<string, string | undefined>>({});
  const [nav, setNav] = useState<NavigationNode[]>([]);

  const getSubsurvey = usePost(`/subsurvey/definition`);
  const getNav = usePost(`/navigation`);
  const searchForDuplicates = usePost(`/applicant/search_for_duplicates`);
  const createApplicant = usePost(`/applicant/create`);

  const shouldFastForward = useRef(true);
  const [redirectToSubsurvey, setRedirectToSubsurvey] = useState<string | undefined>();
  const [warnings, setWarnings] = useState<Warning[]>([]);
  const [readyToSubmit, setReadyToSubmit] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [searchingForDuplicates, setSearchingForDuplicates] = useState(false);

  useAsyncEffect(async () => {
    setLoading(true);
    const [subsurveyResult, navResult] = await Promise.all([
      getSubsurvey({ form_name: subsurveyPath }),
      getNav({})
    ]);

    setNav(navResult.navigation);
    // hacky - borrowed from Apply.tsx
    setSubsurvey(subsurveyResult as unknown as Sections); 
    setLoading(false);
  }, [subsurveyPath]);

  useEffect(() => {
    // Fetch the button id and look for duplicates
    const timeout = setTimeout(async () => {
      if (Object.keys(info).length === 0 || Object.values(info).every(v => !v)) {
        // Don't search if there's no info to search for
        return;
      }

      // We do this in the timeout because otherwise it will constantly be re-rendering when info changes
      setSearchingForDuplicates(true);
      const result = await searchForDuplicates({ buttonId, info });
      console.log("Search for duplicates", result);
      if (result?.redirectToSubsurvey) {
        setRedirectToSubsurvey(result.redirectToSubsurvey);
      }
      if (result?.status === "ok") {
        setWarnings(result.warnings);
      }
      setSearchingForDuplicates(false);
    }, 250);

    return () => clearTimeout(timeout);
  }, [buttonId, info]);

  useEffect(() => {
    // Detect if we're ready to submit
    // Ready means theres no 'cannot add' warnings AND all the required info has been filled out
    const noCannotAddWarnings = warnings.every(warning => !warning.cannotAdd)

    // Filter out sections that are not enabled or visible - 
    // Not visible means it's conditional and the condition is not met
    // As a sanity check, at least one info field should be filled out as well (Can't create an empty applicant)
    const allRequiredFieldsFilled = Object.values(info).some(v => !!v) && subsurvey?.sections?.filter(section => section.currentlyEnabled && section.currentlyVisible).every(section => section.complete);

    // For Debug purposes (Never throw away a good debug tool):
    if (process.env.NODE_ENV === 'development' && !readyToSubmit) {
      console.log("Ready to submit", noCannotAddWarnings, allRequiredFieldsFilled);
      // log allrequired fields why they failed
      if (!allRequiredFieldsFilled) {
        subsurvey?.sections?.forEach(section => {
          if (!section.complete) {
            console.log("Section", section.en, "is not complete", section.currentlyEnabled, section);
            section.Questions.forEach(question => {
              if (!question.complete) {
                console.log("Question", question.Question, "is not complete");
              }
            });
          }
        });
      }
    }
    setReadyToSubmit(Boolean(noCannotAddWarnings && allRequiredFieldsFilled));
  }, [subsurvey, info, warnings, readyToSubmit]);

  const update = useCallback((info: Record<string, string | undefined>) => {
    // only update keys if anything changed
    setInfo(prev => {
      const changed = Object.keys(info).some(key => prev[key] !== info[key]);
      console.log('update', info, changed);
      if (changed) {
        return { ...info };
      }
      return prev;
    })
  }, []);

  const handleSubmit = useCallback(async () => {
    setSubmitting(true);
    console.log("Submitting", info);
    try {
      if (warnings) {
        const noCannotAddWarnings = warnings.every(warning => !warning.cannotAdd)
        if (noCannotAddWarnings) {
          const result = await createApplicant({ 
            legal_name: info["legal_name"] || "New Applicant", 
            ...info 
          });
          if (result?.success) {
            if (redirectToSubsurvey) {
              window.location.href = `/a/${result.newId}/${redirectToSubsurvey}`;
            } else {
              // close the window
              window.alert("Applicant created successfully, closing the window.");
              window.close();
            }
          } else {
            console.error("Error creating applicant", result);
            window.alert("Error creating applicant");
          }
        }
      }
    } finally {
      setSubmitting(false);
    }
  }, [info, warnings, redirectToSubsurvey]);

  const applicantContext = useMemo(() => {
    console.log("Somethig changed, updating context", JSON.stringify(info));
    return {
      info,
      update,
      refreshInfo: async () => {}
    }
  }, [info, update]);

  return <ApplicantProvider context={applicantContext}>
    <ThreeColumnPage
      nav={nav}
      title={"New Applicant" + (info["legal_name"] ? `: ${info["legal_name"]}` : "")}
      main={<Loading loading={loading}>
        {subsurvey && <ModularQuestionPage
          sections={subsurvey}
          uid={'not yet an app homie'}
          submit={async () => { 
            handleSubmit();
          }}
          shouldFastForward={shouldFastForward}
          saveInfo={async () => {}}
          saveAuth={(auth) => {

          }}
          noNavBar={subsurvey ? undefined : true}
          sequential={true}
          Viewer='screener' />}
      </Loading>}
      subtabs={[{
        key: 'next steps',
        name: 'Create Applicant',
        children: <div>
          <h1 className="text-2xl">Duplicate Applicants</h1>
          <p className="text-info text-sm">As you fill out the form, duplicates will show up here, if any are found.</p>
          <Loading loading={searchingForDuplicates} className="my-4" labels={[{ content: "Searching for duplicates..." }]}>
            {warnings.length === 0 && <p className="text-success text-sm">No duplicates found.</p>}
            {warnings.map((warning) => (
              <Alert variant={warning.cannotAdd ? "danger" : "warning"} title={"Warning"} className="w-full m-0 min-w-100" key={warning.warning.en}>
                {warning.warning[lang]}
                {/* A table of applicant and links to their applications */}
                <table>
                  <thead>
                    <tr>
                      <th>Applicant</th>
                      <th>View</th>
                    </tr>
                  </thead>
                  <tbody>
                    {warning.applicants.map((app) => {
                      return <tr key={app.uid}>
                        <td>{app.name}</td>
                        <td><Link to={`/a/${app.uid}`} target="_blank">View</Link></td>
                      </tr>
                    })}
                  </tbody>
                </table>
              </Alert>
            ))}
          </Loading>
          <hr/>
          <h1 className="text-2xl">Next Steps</h1>
          <p className="text-info text-sm">Once you've filled out the form and there are no blocking duplicates, you can submit the application.</p>
          {readyToSubmit ? <Button disabled={submitting} loading={submitting} onClick={handleSubmit}>Submit</Button> : null}
        </div>,
        show: true
      }]}
    />
  </ApplicantProvider>
}